import { h } from 'preact';
import { useEffect, useRef, useState, useMemo } from 'preact/hooks';
import { decodeId } from 'utils/helpers';
import Image from 'utils/image';

const ProductInfoBlock = ({
  type,
  settings,
  currentProduct,
  currentVariant,
}) => {
  if (type === 'sezzle') {
    const sezzle = useRef(null);

    const price =
      currentVariant?.price?.amount ||
      currentProduct.discount?.discountPrice?.amount ||
      currentProduct?.priceRange.minVariantPrice.amount;

    useEffect(() => {
      if (sezzle.current) {
        sezzle.current.alterPrice(`${parseFloat(price)}`);
      }
    }, [price]);

    useEffect(() => {
      sezzle.current = new window.AwesomeSezzle({
        amount: `${price}`,
        renderElement: 'pdp-sezzle',
        alignment: 'left',
        language: window.Shopify.locale,
        widgetTemplate: window.TMW.Strings.product.sezzleMessage
          .replace('{{price}}', '%%price%%')
          .replace('{{logo}}', '%%logo%%'),
      });
      sezzle.current.init();

      const openSezzleButton = document.querySelector('#pdp-sezzle button');

      const sezzleClosedEnableScroll = () => {
        document.body.classList.remove('disabled-scroll');
        const scrollY = Math.abs(parseInt(document.body.style.top, 10));
        document.body.style.top = `0`;
        window.scrollTo(0, scrollY);
        document
          .querySelector('#sezzle-modal-container .close-sezzle-modal')
          ?.removeEventListener('click', sezzleClosedEnableScroll);
        document
          .querySelector('#sezzle-modal-container')
          ?.removeEventListener('click', sezzleClosedEnableScroll);
      };

      const handleOpenSezzleButtonClick = () => {
        const { scrollY } = window;
        document.body.classList.add('disabled-scroll');
        document.body.style.top = `-${scrollY}px`;
        document
          .querySelector('#sezzle-modal-container .close-sezzle-modal')
          ?.addEventListener('click', sezzleClosedEnableScroll);
        document
          .querySelector('#sezzle-modal-container')
          ?.addEventListener('click', sezzleClosedEnableScroll);
      };

      const waitSezzleLoad = setTimeout(() => {
        if (openSezzleButton) {
          openSezzleButton.addEventListener(
            'click',
            handleOpenSezzleButtonClick,
          );
        }
      }, 1000);

      return () => {
        clearTimeout(waitSezzleLoad);

        if (openSezzleButton) {
          openSezzleButton.removeEventListener(
            'click',
            handleOpenSezzleButtonClick,
          );
        }
      };
    }, []);

    return (
      <div className="flex gap-4 md:gap-8 items-start ml:justify-center ml:text-center [&_.sezzle-logo]:!h-13 [&_.sezzle-logo]:!w-auto">
        {!!settings.icon && (
          <Image
            src={settings.icon}
            classes="w-16 md:w-18 block h-auto flex-none"
            width={18}
            height={18}
          />
        )}
        <span
          className="sezzle-message mr-12 cursor-pointer"
          id="pdp-sezzle"
        ></span>
      </div>
    );
  }

  if (type === 'rewards') {
    const source = useRef(null);
    const container = useRef(null);
    useEffect(() => {
      if (window.spapi) {
        window.spapi.setupDisplayPoints();
        setTimeout(() => {
          container.current.innerHTML = source.current.innerHTML;
        }, 200);
      } else {
        container.current.innerHTML = source.current.innerHTML;
      }
    }, [currentProduct]);

    const rewards = useMemo(
      () =>
        settings.text.replace(
          '[points]',
          `<span
            data-mode="display-points"
            class="swell-product-helper"
            data-product-id="${decodeId(currentProduct.id, 'Product')}"
            data-price-cents="${
              currentProduct.priceRange.minVariantPrice.amount * 100
            }"
          ></span>`,
        ),
      [currentProduct],
    );

    return (
      <div className="flex gap-4 md:gap-8 items-start ml:justify-center ml:text-center">
        {!!settings.icon && (
          <Image
            src={settings.icon}
            classes="w-16 md:w-18 block h-auto flex-none"
            width={18}
            height={18}
          />
        )}
        <div
          ref={source}
          className="hidden"
          dangerouslySetInnerHTML={{
            __html: rewards,
          }}
        ></div>
        <div
          ref={container}
          className="body-3 [&_*]:body-3 [&_a]:body-3-underline [&_strong]:body-3-medium [&_strong_*]:body-3-medium [&_a]:hover:no-underline"
        ></div>
      </div>
    );
  }

  return (
    <div className="flex gap-4 md:gap-8 items-start ml:justify-center ml:text-center">
      {!!settings.icon && (
        <Image
          src={settings.icon}
          classes="w-16 md:w-18 block h-auto flex-none"
          width={18}
          height={18}
        />
      )}
      <div
        className="body-3"
        dangerouslySetInnerHTML={{ __html: settings.text }}
      ></div>
    </div>
  );
};

const ProductInfoPanel = (props) => {
  const [settings, setSettings] = useState({});

  useEffect(() => {
    const dataContainer = document.getElementById('product-info-panel');
    if (dataContainer) {
      setSettings(JSON.parse(dataContainer.textContent));
    }
  }, []);

  if (!settings.blocks?.length) {
    return null;
  }

  return (
    <div className="py-32 lg:py-20 ml:-mx-24 ml:md:mx-0 ml:py-16">
      <div className="px-8 py-16 lg:p-24 bg-accent-1 flex flex-col gap-8 lc:rounded-10 ml:p-24 ml:gap-16">
        {settings.blocks?.map((block) => (
          <ProductInfoBlock key={block.id} {...block} {...props} />
        ))}
      </div>
    </div>
  );
};

export default ProductInfoPanel;
